import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
    collection, addDoc, getDocs, deleteDoc, doc, updateDoc,
    query, where, setDoc, getDoc
} from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { db, auth } from '../firebase';

export default function AdminDashboard() {
    const { currentUser, userProfile, logout } = useAuth();
    const navigate = useNavigate();

    const [games, setGames] = useState([]);
    const [users, setUsers] = useState([]);
    const [hostRequests, setHostRequests] = useState([]);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [editingGame, setEditingGame] = useState(null);
    const [editingUser, setEditingUser] = useState(null);
    const [showCreateUser, setShowCreateUser] = useState(false);
    const [editingHostRequest, setEditingHostRequest] = useState(null);
    const [loading, setLoading] = useState(true);
    const [hostRequestEmailEnabled, setHostRequestEmailEnabled] = useState(true);

    useEffect(() => {
        if (!userProfile?.isAdmin) {
            navigate('/dashboard');
            return;
        }
        loadData();
    }, [userProfile]);

    async function loadData() {
        try {
            // Load games
            const gamesSnapshot = await getDocs(collection(db, 'games'));
            const gamesData = gamesSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // Sort by date, most recent first
            gamesData.sort((a, b) => {
                if (!a.date || !b.date) return 0;
                return b.date.seconds - a.date.seconds;
            });

            setGames(gamesData);

            const usersSnapshot = await getDocs(collection(db, 'users'));
            const usersData = usersSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setUsers(usersData);

            // Load host requests
            const requestsSnapshot = await getDocs(
                query(collection(db, 'hostRequests'), where('status', '==', 'pending'))
            );
            const requestsData = requestsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setHostRequests(requestsData);

            // Load settings
            const settingsDoc = await getDoc(doc(db, 'settings', 'notifications'));
            if (settingsDoc.exists()) {
                const settingsData = settingsDoc.data();
                setHostRequestEmailEnabled(settingsData.hostRequestEmailEnabled !== false);
            }

            setLoading(false);
        } catch (err) {
            console.error('Error loading data:', err);
            setLoading(false);
        }
    }

    async function handleLogout() {
        try {
            await logout();
            navigate('/login');
        } catch (err) {
            console.error('Failed to log out', err);
        }
    }

    async function handleDeleteGame(gameId) {
        if (window.confirm('האם אתה בטוח שברצונך למחוק משחק זה?')) {
            try {
                await deleteDoc(doc(db, 'games', gameId));
                await loadData();
            } catch (err) {
                console.error('Error deleting game:', err);
                alert('שגיאה במחיקת משחק: ' + err.message);
            }
        }
    }

    async function handleDeleteUser(userId, userName) {
        if (userId === currentUser.uid) {
            alert('לא ניתן למחוק את המשתמש שלך');
            return;
        }

        if (window.confirm(`האם אתה בטוח שברצונך למחוק את המשתמש "${userName}"?\n\nשים לב: זה ימחק את המשתמש מהמערכת אך לא ימחק את חשבון ההתחברות שלו.`)) {
            try {
                // Delete user document from Firestore
                await deleteDoc(doc(db, 'users', userId));

                // Remove user from all games
                const gamesSnapshot = await getDocs(collection(db, 'games'));
                const updatePromises = [];

                gamesSnapshot.docs.forEach(gameDoc => {
                    const gameData = gameDoc.data();
                    const players = gameData.players || [];
                    const waitlist = gameData.waitlist || [];

                    if (players.includes(userId) || waitlist.includes(userId)) {
                        updatePromises.push(
                            updateDoc(doc(db, 'games', gameDoc.id), {
                                players: players.filter(id => id !== userId),
                                waitlist: waitlist.filter(id => id !== userId)
                            })
                        );
                    }
                });

                await Promise.all(updatePromises);
                await loadData();
                alert('המשתמש נמחק בהצלחה');
            } catch (err) {
                console.error('Error deleting user:', err);
                alert('שגיאה במחיקת משתמש: ' + err.message);
            }
        }
    }

    async function handleApproveHostRequest(request) {
        try {
            // Mark request as approved
            await updateDoc(doc(db, 'hostRequests', request.id), {
                status: 'approved',
                approvedAt: new Date(),
                approvedBy: currentUser.uid
            });

            // Pre-select the host for the create form and add as first player
            setShowCreateForm({
                prefilledDate: request.requestedDate,
                preselectedHost: request.userId,
                preselectedPlayers: [request.userId] // Host as first player
            });

            await loadData();
        } catch (err) {
            alert('שגיאה באישור בקשה: ' + err.message);
        }
    }

    async function handleRejectHostRequest(requestId) {
        if (window.confirm('האם אתה בטוח שברצונך לדחות בקשה זו?')) {
            try {
                await updateDoc(doc(db, 'hostRequests', requestId), {
                    status: 'rejected',
                    rejectedAt: new Date(),
                    rejectedBy: currentUser.uid
                });
                await loadData();
            } catch (err) {
                alert('שגיאה בדחיית בקשה: ' + err.message);
            }
        }
    }

    async function handleToggleHostingPlayerTag(userId, currentlyTagged, userName) {
        const action = currentlyTagged ? 'הסרת' : 'הוספת';
        const actionQuestion = currentlyTagged
            ? `האם אתה בטוח שברצונך להסיר את תג המארח מ-${userName}?`
            : `האם אתה בטוח שברצונך לתת תג מארח ל-${userName}? (תקף 2 חודשים)`;

        if (window.confirm(actionQuestion)) {
            try {
                if (currentlyTagged) {
                    // Remove hosting player tag
                    await updateDoc(doc(db, 'users', userId), {
                        hostingPlayerTag: false,
                        hostingPlayerTagExpiry: null
                    });
                } else {
                    // Add hosting player tag with 2-month expiry
                    const expiryDate = new Date();
                    expiryDate.setMonth(expiryDate.getMonth() + 2);

                    await updateDoc(doc(db, 'users', userId), {
                        hostingPlayerTag: true,
                        hostingPlayerTagExpiry: expiryDate,
                        lastHostedAt: new Date()
                    });
                }

                await loadData();
                alert(`${action} תג המארח בוצע בהצלחה`);
            } catch (err) {
                alert(`שגיאה ב${action} תג מארח: ` + err.message);
            }
        }
    }

    async function handleToggleHostRequestEmail() {
        try {
            const newValue = !hostRequestEmailEnabled;
            await setDoc(doc(db, 'settings', 'notifications'), {
                hostRequestEmailEnabled: newValue,
                updatedAt: new Date(),
                updatedBy: currentUser.uid
            }, { merge: true });
            setHostRequestEmailEnabled(newValue);
        } catch (err) {
            console.error('Error updating email setting:', err);
            alert('שגיאה בעדכון הגדרות: ' + err.message);
        }
    }

    if (loading) {
        return <div style={styles.loading}>טוען...</div>;
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1>👨‍💼 ניהול מערכת</h1>
                <div style={styles.headerButtons}>
                    <button onClick={() => navigate('/dashboard')} style={styles.dashboardButton}>
                        ← חזרה לדשבורד
                    </button>
                    <button onClick={handleLogout} style={styles.logoutButton}>
                        התנתק
                    </button>
                </div>
            </div>

            <div style={styles.content}>
                {/* Host Requests Section */}
                {hostRequests.length > 0 && (
                    <div style={styles.section}>
                        <h2 style={styles.alertTitle}>🏠 בקשות אירוח ממתינות ({hostRequests.length})</h2>
                        <div style={styles.requestsList}>
                            {hostRequests.map(request => (
                                <HostRequestCard
                                    key={request.id}
                                    request={request}
                                    users={users}
                                    onApprove={() => handleApproveHostRequest(request)}
                                    onReject={() => handleRejectHostRequest(request.id)}
                                    onEdit={() => setEditingHostRequest(request)}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {/* Games Section */}
                <div style={styles.section}>
                    <div style={styles.sectionHeader}>
                        <h2 style={{color: '#ffd700', textShadow: '0 2px 10px rgba(255, 215, 0, 0.3)', fontSize: '24px'}}>🎲 משחקים ({games.length})</h2>
                        <button
                            onClick={() => setShowCreateForm(true)}
                            style={styles.createButton}
                        >
                            + צור משחק חדש
                        </button>
                    </div>

                    {showCreateForm && (
                        <GameForm
                            users={users}
                            currentUser={currentUser}
                            prefilled={typeof showCreateForm === 'object' ? showCreateForm : null}
                            onClose={() => setShowCreateForm(false)}
                            onSuccess={() => {
                                setShowCreateForm(false);
                                loadData();
                            }}
                        />
                    )}

                    {editingGame && (
                        <GameForm
                            game={editingGame}
                            users={users}
                            currentUser={currentUser}
                            onClose={() => setEditingGame(null)}
                            onSuccess={() => {
                                setEditingGame(null);
                                loadData();
                            }}
                        />
                    )}

                    {games.length === 0 ? (
                        <p style={styles.emptyText}>אין משחקים עדיין. צור את המשחק הראשון!</p>
                    ) : (
                        <div style={styles.gamesList}>
                            {games.map(game => (
                                <AdminGameCard
                                    key={game.id}
                                    game={game}
                                    users={users}
                                    onDelete={handleDeleteGame}
                                    onEdit={() => setEditingGame(game)}
                                    onView={() => navigate(`/game/${game.id}`)}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* Users Section */}
                <div style={styles.section}>
                    <div style={styles.sectionHeader}>
                        <h2 style={{color: '#ffd700', textShadow: '0 2px 10px rgba(255, 215, 0, 0.3)', fontSize: '24px'}}>👥 משתמשים ({users.length})</h2>
                        <button
                            onClick={() => setShowCreateUser(true)}
                            style={styles.createButton}
                        >
                            + צור משתמש חדש
                        </button>
                    </div>
                    <div style={styles.usersList}>
                        {users.map(user => (
                            <div key={user.id} style={styles.userCard}>
                                <div style={styles.userCardContent}>
                                    <div>
                                        <strong>{user.displayName}</strong>
                                        <br />
                                        <small style={styles.userEmail}>{user.email}</small>
                                        {user.address && (
                                            <>
                                                <br />
                                                <small style={styles.userAddress}>📍 {user.address}</small>
                                            </>
                                        )}
                                        {user.hostingPlayerTag &&
                                         user.hostingPlayerTagExpiry &&
                                         new Date(user.hostingPlayerTagExpiry.seconds * 1000) > new Date() && (
                                            <>
                                                <br />
                                                <small style={styles.hostingPlayerBadgeSmall}>
                                                    🏠 מארח פעיל עד {new Date(user.hostingPlayerTagExpiry.seconds * 1000).toLocaleDateString('he-IL', {
                                                        day: 'numeric',
                                                        month: 'short'
                                                    })}
                                                </small>
                                            </>
                                        )}
                                    </div>
                                    <div style={styles.userCardActions}>
                                        {user.isAdmin && <span style={styles.adminBadge}>מנהל</span>}
                                        <button
                                            onClick={() => handleToggleHostingPlayerTag(
                                                user.id,
                                                user.hostingPlayerTag && user.hostingPlayerTagExpiry && new Date(user.hostingPlayerTagExpiry.seconds * 1000) > new Date(),
                                                user.displayName
                                            )}
                                            style={
                                                user.hostingPlayerTag && user.hostingPlayerTagExpiry && new Date(user.hostingPlayerTagExpiry.seconds * 1000) > new Date()
                                                    ? styles.hostingTagButtonActive
                                                    : styles.hostingTagButton
                                            }
                                            title={
                                                user.hostingPlayerTag && user.hostingPlayerTagExpiry && new Date(user.hostingPlayerTagExpiry.seconds * 1000) > new Date()
                                                    ? "הסר תג מארח"
                                                    : "תן תג מארח (2 חודשים)"
                                            }
                                        >
                                            🏠
                                        </button>
                                        <button
                                            onClick={() => setEditingUser(user)}
                                            style={styles.editUserButton}
                                            title="ערוך משתמש"
                                        >
                                            ✏️
                                        </button>
                                        <button
                                            onClick={() => handleDeleteUser(user.id, user.displayName)}
                                            style={styles.deleteUserButton}
                                            title="מחק משתמש"
                                        >
                                            🗑️
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Settings Section */}
                <div style={styles.section}>
                    <h2 style={{color: '#ffd700', textShadow: '0 2px 10px rgba(255, 215, 0, 0.3)', fontSize: '24px', marginBottom: '20px'}}>⚙️ הגדרות</h2>
                    <div style={styles.settingsBar}>
                        <div style={styles.settingItem}>
                            <span style={styles.settingLabel}>📧 התראות אימייל על בקשות אירוח:</span>
                            <button
                                onClick={handleToggleHostRequestEmail}
                                style={hostRequestEmailEnabled ? styles.toggleButtonOn : styles.toggleButtonOff}
                            >
                                {hostRequestEmailEnabled ? '✓ פעיל' : '✕ כבוי'}
                            </button>
                        </div>
                    </div>
                </div>

                {editingUser && (
                    <EditUserModal
                        user={editingUser}
                        onClose={() => setEditingUser(null)}
                        onSuccess={() => {
                            setEditingUser(null);
                            loadData();
                        }}
                    />
                )}

                {showCreateUser && (
                    <CreateUserModal
                        onClose={() => setShowCreateUser(false)}
                        onSuccess={() => {
                            setShowCreateUser(false);
                            loadData();
                        }}
                    />
                )}

                {editingHostRequest && (
                    <EditHostRequestModal
                        request={editingHostRequest}
                        users={users}
                        onClose={() => setEditingHostRequest(null)}
                        onSuccess={() => {
                            setEditingHostRequest(null);
                            loadData();
                        }}
                    />
                )}
            </div>
        </div>
    );
}

function HostRequestCard({ request, users, onApprove, onReject, onEdit }) {
    const user = users.find(u => u.id === request.userId);

    return (
        <div style={styles.requestCard}>
            <div style={styles.requestInfo}>
                <h3 style={styles.requestUser}>{user?.displayName || 'משתמש לא ידוע'}</h3>
                <p style={styles.requestDate}>
                    📅 {new Date(request.requestedDate.seconds * 1000).toLocaleDateString('he-IL', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                })}
                </p>
                {request.message && (
                    <p style={styles.requestMessage}>💬 {request.message}</p>
                )}
                <p style={styles.requestTime}>
                    התקבל ב-{new Date(request.createdAt.seconds * 1000).toLocaleDateString('he-IL')}
                </p>
            </div>
            <div style={styles.requestActions}>
                <button onClick={onEdit} style={styles.editRequestButton}>
                    ✏️ ערוך
                </button>
                <button onClick={onApprove} style={styles.approveButton}>
                    ✓ אשר
                </button>
                <button onClick={onReject} style={styles.rejectButton}>
                    ✕ דחה
                </button>
            </div>
        </div>
    );
}

function EditHostRequestModal({ request, users, onClose, onSuccess }) {
    const user = users.find(u => u.id === request.userId);

    // Format date for datetime-local input
    const formatDateForInput = (date) => {
        if (!date) return '';
        const d = date.seconds ? new Date(date.seconds * 1000) : new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    const [requestedDate, setRequestedDate] = useState(formatDateForInput(request.requestedDate));
    const [message, setMessage] = useState(request.message || '');
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    async function handleSubmit(e) {
        e.preventDefault();
        setError('');

        if (!requestedDate) {
            setError('אנא בחר תאריך');
            return;
        }

        try {
            setSaving(true);

            await updateDoc(doc(db, 'hostRequests', request.id), {
                requestedDate: new Date(requestedDate),
                message: message.trim(),
                updatedAt: new Date()
            });

            onSuccess();
        } catch (err) {
            setError('שגיאה בעדכון בקשה: ' + err.message);
            setSaving(false);
        }
    }

    return (
        <div style={styles.modal}>
            <div style={styles.modalContent}>
                <div style={styles.modalHeader}>
                    <h2>ערוך בקשת אירוח</h2>
                    <button onClick={onClose} style={styles.closeButton}>✕</button>
                </div>

                {error && <div style={styles.error}>{error}</div>}

                <div style={styles.infoAlert}>
                    מבקש: {user?.displayName || 'משתמש לא ידוע'}
                </div>

                <form onSubmit={handleSubmit}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>תאריך מבוקש *</label>
                        <input
                            type="datetime-local"
                            value={requestedDate}
                            onChange={(e) => setRequestedDate(e.target.value)}
                            style={styles.input}
                            required
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>הודעה</label>
                        <textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="הודעה מהמבקש..."
                            style={{...styles.input, minHeight: '80px', resize: 'vertical'}}
                            dir="rtl"
                        />
                    </div>

                    <div style={styles.modalActions}>
                        <button
                            type="button"
                            onClick={onClose}
                            style={styles.cancelButton}
                            disabled={saving}
                        >
                            ביטול
                        </button>
                        <button
                            type="submit"
                            style={styles.saveButton}
                            disabled={saving}
                        >
                            {saving ? 'שומר...' : 'שמור שינויים'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function GameForm({ game, users, currentUser, prefilled, onClose, onSuccess }) {
    const isEdit = !!game;

    // Get default date with time set to 21:00
    const getDefaultDate = () => {
        if (game?.date) {
            const d = new Date(game.date.seconds * 1000);
            // Format for datetime-local input using local timezone
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            const hours = String(d.getHours()).padStart(2, '0');
            const minutes = String(d.getMinutes()).padStart(2, '0');
            return `${year}-${month}-${day}T${hours}:${minutes}`;
        }
        if (prefilled?.prefilledDate) {
            const d = new Date(prefilled.prefilledDate.seconds * 1000);
            // Format for datetime-local input using local timezone
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            const hours = String(d.getHours()).padStart(2, '0');
            const minutes = String(d.getMinutes()).padStart(2, '0');
            return `${year}-${month}-${day}T${hours}:${minutes}`;
        }
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(21, 0, 0, 0);
        // Format for datetime-local input using local timezone
        const year = tomorrow.getFullYear();
        const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
        const day = String(tomorrow.getDate()).padStart(2, '0');
        const hours = String(tomorrow.getHours()).padStart(2, '0');
        const minutes = String(tomorrow.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    const [date, setDate] = useState(getDefaultDate());

    // Get default registration open time (defaults to first Sunday before game at 12:00 noon)
    const getDefaultRegistrationTime = () => {
        if (game?.registrationOpenTime) {
            const d = new Date(game.registrationOpenTime.seconds * 1000);
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            const hours = String(d.getHours()).padStart(2, '0');
            const minutes = String(d.getMinutes()).padStart(2, '0');
            return `${year}-${month}-${day}T${hours}:${minutes}`;
        }

        // Calculate first Sunday before the game at 12:00 noon
        const gameDate = date ? new Date(date) : new Date();
        const registrationDate = new Date(gameDate);

        // Find the Sunday before the game date
        // If game is on Sunday, go back to previous Sunday
        const gameDayOfWeek = registrationDate.getDay(); // 0 = Sunday, 1 = Monday, etc.

        if (gameDayOfWeek === 0) {
            // Game is on Sunday, go back 7 days to previous Sunday
            registrationDate.setDate(registrationDate.getDate() - 7);
        } else {
            // Go back to the most recent Sunday
            registrationDate.setDate(registrationDate.getDate() - gameDayOfWeek);
        }

        // Set time to 12:00 noon
        registrationDate.setHours(12, 0, 0, 0);

        // Format for datetime-local input
        const year = registrationDate.getFullYear();
        const month = String(registrationDate.getMonth() + 1).padStart(2, '0');
        const day = String(registrationDate.getDate()).padStart(2, '0');
        const hours = String(registrationDate.getHours()).padStart(2, '0');
        const minutes = String(registrationDate.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    const [registrationOpenTime, setRegistrationOpenTime] = useState(getDefaultRegistrationTime());
    const [selectedHost, setSelectedHost] = useState(
        game?.hostUserId ||
        prefilled?.preselectedHost ||
        ''
    );

    // Initialize selected players - include host from approved request
    const getInitialPlayers = () => {
        if (game?.players) return game.players;

        // If creating from approved host request, include preselected players (host)
        if (prefilled?.preselectedPlayers) return prefilled.preselectedPlayers;

        return [];
    };

    const [selectedPlayers, setSelectedPlayers] = useState(getInitialPlayers());
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [registrationTimeManuallySet, setRegistrationTimeManuallySet] = useState(false);

    // Update registration time when date changes (only for new games and if not manually set)
    useEffect(() => {
        if (!game && date && !registrationTimeManuallySet) {
            // Calculate first Sunday before the game at 12:00 noon
            const gameDate = new Date(date);
            const registrationDate = new Date(gameDate);

            // Find the Sunday before the game date
            const gameDayOfWeek = registrationDate.getDay(); // 0 = Sunday, 1 = Monday, etc.

            if (gameDayOfWeek === 0) {
                // Game is on Sunday, go back 7 days to previous Sunday
                registrationDate.setDate(registrationDate.getDate() - 7);
            } else {
                // Go back to the most recent Sunday
                registrationDate.setDate(registrationDate.getDate() - gameDayOfWeek);
            }

            // Set time to 12:00 noon
            registrationDate.setHours(12, 0, 0, 0);

            // Format for datetime-local input
            const year = registrationDate.getFullYear();
            const month = String(registrationDate.getMonth() + 1).padStart(2, '0');
            const day = String(registrationDate.getDate()).padStart(2, '0');
            const hours = String(registrationDate.getHours()).padStart(2, '0');
            const minutes = String(registrationDate.getMinutes()).padStart(2, '0');
            setRegistrationOpenTime(`${year}-${month}-${day}T${hours}:${minutes}`);
        }
    }, [date, game, registrationTimeManuallySet]);

    // Function to tag a user as hosting player
    async function tagHostingPlayer(userId) {
        if (!userId) return;

        try {
            const expiryDate = new Date();
            expiryDate.setMonth(expiryDate.getMonth() + 2); // 2 months from now

            await updateDoc(doc(db, 'users', userId), {
                hostingPlayerTag: true,
                hostingPlayerTagExpiry: expiryDate
            });

            console.log(`Tagged user ${userId} as hosting player until ${expiryDate}`);
        } catch (err) {
            console.error('Error tagging hosting player:', err);
            // Don't throw - this shouldn't block game creation
        }
    }

    // Get host user object
    const hostUser = selectedHost ? users.find(u => u.id === selectedHost) : null;

    // Auto-generate game name and get host details
    const [name, setName] = useState(game?.name || (hostUser ? `פוקר אצל ${hostUser.displayName}` : ''));
    const [address, setAddress] = useState(game?.address || (hostUser?.address || ''));
    const [notes, setNotes] = useState(game?.notes || (hostUser?.notes || ''));

    // Update address, notes, and players when host changes (only for new games)
    useEffect(() => {
        if (!game && selectedHost) {
            const host = users.find(u => u.id === selectedHost);
            if (host) {
                // Update address if host has one
                if (host.address) {
                    setAddress(host.address);
                }
                // Update notes if host has them
                if (host.notes) {
                    setNotes(host.notes);
                }
                // Update name with host name
                setName(`פוקר אצל ${host.displayName}`);
                // Auto-add host to players list if not already there
                if (!selectedPlayers.includes(selectedHost)) {
                    setSelectedPlayers(prev => prev.includes(selectedHost) ? prev : [selectedHost, ...prev]);
                }
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedHost, game, users]);

    function togglePlayer(userId) {
        // Prevent unchecking the host
        if (userId === selectedHost && selectedPlayers.includes(userId)) {
            setError('לא ניתן להסיר את המארח מרשימת השחקנים');
            return;
        }

        if (selectedPlayers.includes(userId)) {
            setSelectedPlayers(selectedPlayers.filter(id => id !== userId));
        } else {
            if (selectedPlayers.length < 10) {
                setSelectedPlayers([...selectedPlayers, userId]);
            } else {
                setError('מקסימום 10 שחקנים יכולים להירשם מראש');
            }
        }
    }


    async function handleSubmit(e) {
        e.preventDefault();
        setError('');

        if (!selectedHost) {
            setError('אנא בחר מארח');
            return;
        }

        if (!date) {
            setError('אנא בחר תאריך');
            return;
        }

        try {
            setSaving(true);

            // Ensure host is included in players list
            let finalPlayers = [...selectedPlayers];
            if (!finalPlayers.includes(selectedHost)) {
                finalPlayers = [selectedHost, ...finalPlayers];
            }

            // Auto-generate game name if not provided
            let gameName = name.trim();
            if (!gameName) {
                const gameDate = new Date(date);
                const dateStr = gameDate.toLocaleDateString('he-IL', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long'
                });
                if (selectedHost) {
                    const host = users.find(u => u.id === selectedHost);
                    gameName = host ? `פוקר אצל ${host.displayName}` : `משחק פוקר - ${dateStr}`;
                } else {
                    gameName = `משחק פוקר - ${dateStr}`;
                }
            }

            const gameData = {
                name: gameName,
                date: new Date(date),
                registrationOpenTime: new Date(registrationOpenTime),
                address: address.trim(),
                notes: notes.trim(),
                hostUserId: selectedHost,
                players: finalPlayers,
                maxPlayers: 10
            };

            if (game) {
                await updateDoc(doc(db, 'games', game.id), gameData);

                // If host changed, update hosting player tags
                if (selectedHost && selectedHost !== game.hostUserId) {
                    await tagHostingPlayer(selectedHost);
                }
            } else {
                await addDoc(collection(db, 'games'), {
                    ...gameData,
                    createdBy: currentUser.uid,
                    createdAt: new Date(),
                    status: 'open'
                });

                // Tag the host as a hosting player
                if (selectedHost) {
                    await tagHostingPlayer(selectedHost);
                }
            }

            onSuccess();
        } catch (err) {
            setError('שגיאה בשמירת משחק: ' + err.message);
            setSaving(false);
        }
    }

    return (
        <div style={styles.modal}>
            <div style={styles.modalContent}>
                <div style={styles.modalHeader}>
                    <h2>{isEdit ? 'ערוך משחק' : 'צור משחק חדש'}</h2>
                    <button onClick={onClose} style={styles.closeButton}>✕</button>
                </div>

                {error && <div style={styles.error}>{error}</div>}


                <form onSubmit={handleSubmit}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>מארח *</label>
                        <select
                            value={selectedHost}
                            onChange={(e) => setSelectedHost(e.target.value)}
                            style={styles.input}
                            required
                        >
                            <option value="">בחר מארח</option>
                            {users.map(user => (
                                <option key={user.id} value={user.id}>
                                    {user.displayName}
                                </option>
                            ))}
                        </select>
                        <small style={styles.helpText}>
                            המארח יירשם אוטומטית כשחקן במשחק. בחירת מארח תמלא את שם המשחק, כתובת והערות.
                        </small>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>תאריך ושעה *</label>
                        <input
                            type="datetime-local"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            style={styles.input}
                            required
                        />
                        <small style={styles.helpText}>ברירת מחדל: 21:00</small>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>תאריך פתיחת הרשמה *</label>
                        <input
                            type="datetime-local"
                            value={registrationOpenTime}
                            onChange={(e) => {
                                setRegistrationOpenTime(e.target.value);
                                setRegistrationTimeManuallySet(true);
                            }}
                            style={styles.input}
                            required
                        />
                        <small style={styles.helpText}>ברירת מחדל: יום ראשון קודם בשעה 12:00</small>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>כתובת</label>
                        <input
                            type="text"
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                            placeholder="רחוב, עיר, מיקוד"
                            style={styles.input}
                            dir="rtl"
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>הערות</label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="קוד כניסה, קומה, מספר דירה, הנחיות חניה וכו׳"
                            style={{...styles.input, minHeight: '80px', resize: 'vertical'}}
                            dir="rtl"
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>
                            שחקנים רשומים מראש ({selectedPlayers.length}/10)
                        </label>
                        <p style={styles.helpText}>
                            בחר שחקנים שכבר אישרו השתתפות במשחק
                        </p>

                        <div style={styles.playersList}>
                            {users.map(user => (
                                <label key={user.id} style={styles.playerCheckbox}>
                                    <input
                                        type="checkbox"
                                        checked={selectedPlayers.includes(user.id)}
                                        onChange={() => togglePlayer(user.id)}
                                        disabled={selectedPlayers.length >= 10 && !selectedPlayers.includes(user.id)}
                                    />
                                    <span style={styles.playerCheckboxLabel}>
                    {user.displayName}
                                        {user.id === selectedHost && <span style={styles.hostBadgeSmall}>מארח</span>}
                                        <small style={styles.playerCheckboxEmail}>{user.email}</small>
                  </span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div style={styles.modalActions}>
                        <button
                            type="button"
                            onClick={onClose}
                            style={styles.cancelButton}
                            disabled={saving}
                        >
                            ביטול
                        </button>
                        <button
                            type="submit"
                            style={styles.saveButton}
                            disabled={saving}
                        >
                            {saving ? 'שומר...' : (isEdit ? 'שמור שינויים' : 'צור משחק')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function CreateUserModal({ onClose, onSuccess }) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [address, setAddress] = useState('');
    const [notes, setNotes] = useState('');
    const [isAdmin, setIsAdmin] = useState(false);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    async function handleSubmit(e) {
        e.preventDefault();
        setError('');

        if (password.length < 6) {
            setError('הסיסמה חייבת להיות לפחות 6 תווים');
            return;
        }

        try {
            setSaving(true);

            // Create user in Firebase Auth
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            // Create user profile in Firestore
            await setDoc(doc(db, 'users', user.uid), {
                email: email.trim(),
                displayName: displayName.trim(),
                address: address.trim(),
                notes: notes.trim(),
                isAdmin: isAdmin,
                createdAt: new Date()
            });

            onSuccess();
        } catch (err) {
            console.error('Error creating user:', err);
            let errorMessage = 'שגיאה ביצירת משתמש: ';

            switch (err.code) {
                case 'auth/email-already-in-use':
                    errorMessage += 'כתובת האימייל כבר בשימוש';
                    break;
                case 'auth/invalid-email':
                    errorMessage += 'כתובת אימייל לא תקינה';
                    break;
                case 'auth/weak-password':
                    errorMessage += 'הסיסמה חלשה מדי';
                    break;
                default:
                    errorMessage += err.message;
            }

            setError(errorMessage);
            setSaving(false);
        }
    }

    return (
        <div style={styles.modal}>
            <div style={styles.modalContent}>
                <div style={styles.modalHeader}>
                    <h2>צור משתמש חדש</h2>
                    <button onClick={onClose} style={styles.closeButton}>✕</button>
                </div>

                {error && <div style={styles.error}>{error}</div>}

                <form onSubmit={handleSubmit}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>אימייל *</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="user@example.com"
                            required
                            style={styles.input}
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>סיסמה *</label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="לפחות 6 תווים"
                            required
                            minLength={6}
                            style={styles.input}
                        />
                        <small style={styles.helpText}>לפחות 6 תווים</small>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>שם מלא *</label>
                        <input
                            type="text"
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                            placeholder="שם מלא"
                            required
                            style={styles.input}
                            dir="rtl"
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>כתובת לאירוח</label>
                        <input
                            type="text"
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                            placeholder="רחוב, עיר"
                            style={styles.input}
                            dir="rtl"
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>הערות לאירוח</label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="קוד כניסה, קומה, מספר דירה וכו׳"
                            style={{...styles.input, minHeight: '80px', resize: 'vertical'}}
                            dir="rtl"
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={isAdmin}
                                onChange={(e) => setIsAdmin(e.target.checked)}
                            />
                            <span style={{marginRight: '8px'}}>מנהל מערכת</span>
                        </label>
                    </div>

                    <div style={styles.modalActions}>
                        <button
                            type="button"
                            onClick={onClose}
                            style={styles.cancelButton}
                            disabled={saving}
                        >
                            ביטול
                        </button>
                        <button
                            type="submit"
                            style={styles.saveButton}
                            disabled={saving}
                        >
                            {saving ? 'יוצר...' : 'צור משתמש'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}


function EditUserModal({ user, onClose, onSuccess }) {
    const [displayName, setDisplayName] = useState(user.displayName || '');
    const [address, setAddress] = useState(user.address || '');
    const [notes, setNotes] = useState(user.notes || '');
    const [isAdmin, setIsAdmin] = useState(user.isAdmin || false);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    async function handleSubmit(e) {
        e.preventDefault();
        setError('');

        try {
            setSaving(true);

            await updateDoc(doc(db, 'users', user.id), {
                displayName: displayName.trim(),
                address: address.trim(),
                notes: notes.trim(),
                isAdmin: isAdmin
            });

            onSuccess();
        } catch (err) {
            setError('שגיאה בעדכון משתמש: ' + err.message);
            setSaving(false);
        }
    }

    return (
        <div style={styles.modal}>
            <div style={styles.modalContent}>
                <div style={styles.modalHeader}>
                    <h2>ערוך משתמש</h2>
                    <button onClick={onClose} style={styles.closeButton}>✕</button>
                </div>

                {error && <div style={styles.error}>{error}</div>}

                <form onSubmit={handleSubmit}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>שם מלא *</label>
                        <input
                            type="text"
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                            required
                            style={styles.input}
                            dir="rtl"
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>אימייל</label>
                        <input
                            type="email"
                            value={user.email}
                            disabled
                            style={{...styles.input, backgroundColor: '#f0f2f5', color: '#333'}}
                        />
                        <small style={styles.helpText}>לא ניתן לשנות אימייל</small>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>כתובת לאירוח</label>
                        <input
                            type="text"
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                            placeholder="רחוב, עיר"
                            style={styles.input}
                            dir="rtl"
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>הערות לאירוח</label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="קוד כניסה, קומה, מספר דירה וכו׳"
                            style={{...styles.input, minHeight: '80px', resize: 'vertical'}}
                            dir="rtl"
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={isAdmin}
                                onChange={(e) => setIsAdmin(e.target.checked)}
                            />
                            <span style={{marginRight: '8px'}}>מנהל מערכת</span>
                        </label>
                    </div>

                    <div style={styles.modalActions}>
                        <button
                            type="button"
                            onClick={onClose}
                            style={styles.cancelButton}
                            disabled={saving}
                        >
                            ביטול
                        </button>
                        <button
                            type="submit"
                            style={styles.saveButton}
                            disabled={saving}
                        >
                            {saving ? 'שומר...' : 'שמור שינויים'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function AdminGameCard({ game, users, onDelete, onEdit, onView }) {
    const playerCount = game.players?.length || 0;
    const waitlistCount = game.waitlist?.length || 0;
    const host = game.hostUserId ? users.find(u => u.id === game.hostUserId) : null;

    const playerNames = (game.players || [])
        .map(id => users.find(u => u.id === id)?.displayName || 'לא ידוע')
        .slice(0, 3);

    return (
        <div style={styles.gameCard}>
            <div style={styles.gameCardHeader}>
                <h3 style={styles.gameCardTitle}>
                    {game.name}
                    {host && <span style={styles.hostBadge}>🏠 {host.displayName}</span>}
                </h3>
                <div style={styles.gameCardActions}>
                    <button onClick={onEdit} style={styles.editButton}>✏️</button>
                    <button onClick={onView} style={styles.viewButton}>צפה</button>
                    <button onClick={() => onDelete(game.id)} style={styles.deleteButton}>
                        🗑️
                    </button>
                </div>
            </div>

            {game.date && (
                <p style={styles.gameCardDate}>
                    📅 {new Date(game.date.seconds * 1000).toLocaleDateString('he-IL', {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit'
                })}
                </p>
            )}

            {game.address && (
                <p style={styles.gameCardAddress}>📍 {game.address}</p>
            )}

            <div style={styles.gameCardStats}>
                <span>👥 {playerCount}/10 שחקנים</span>
                {waitlistCount > 0 && <span>⏳ {waitlistCount} ממתינים</span>}
            </div>

            {playerNames.length > 0 && (
                <p style={styles.gameCardPlayers}>
                    שחקנים: {playerNames.join(', ')}
                    {playerCount > 3 && ` +${playerCount - 3} נוספים`}
                </p>
            )}

            <div style={styles.gameCardLink}>
                <input
                    type="text"
                    value={`${window.location.origin}/game/${game.id}`}
                    readOnly
                    style={styles.linkInput}
                    onClick={(e) => e.target.select()}
                />
                <button
                    onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/game/${game.id}`);
                        alert('הקישור הועתק!');
                    }}
                    style={styles.copyButton}
                >
                    📋 העתק
                </button>
            </div>
        </div>
    );
}

const styles = {
    container: {
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
        direction: 'rtl',
    },
    settingsBar: {
        backgroundColor: 'rgba(26, 26, 46, 0.8)',
        padding: '15px 20px',
        borderRadius: '12px',
        marginBottom: '25px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '20px',
        border: '1px solid rgba(218, 165, 32, 0.3)',
    },
    settingItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
    },
    settingLabel: {
        color: '#e0e0e0',
        fontSize: '14px',
        fontWeight: '500',
    },
    toggleButtonOn: {
        padding: '8px 16px',
        background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        fontWeight: 'bold',
        fontSize: '13px',
        transition: 'all 0.3s ease',
        boxShadow: '0 2px 8px rgba(22, 163, 74, 0.3)',
    },
    toggleButtonOff: {
        padding: '8px 16px',
        background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        fontWeight: 'bold',
        fontSize: '13px',
        transition: 'all 0.3s ease',
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
    },
    header: {
        backgroundColor: '#1a1a2e',
        color: '#ffd700',
        padding: '20px 30px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '20px',
        borderBottom: '2px solid rgba(218, 165, 32, 0.3)',
        boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
    },
    headerButtons: {
        display: 'flex',
        gap: '10px',
    },
    dashboardButton: {
        padding: '10px 18px',
        background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '10px',
        cursor: 'pointer',
        fontWeight: 'bold',
        transition: 'all 0.3s ease',
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
    },
    logoutButton: {
        padding: '10px 18px',
        background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '10px',
        cursor: 'pointer',
        fontWeight: 'bold',
        transition: 'all 0.3s ease',
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
    },
    content: {
        padding: '30px 20px',
        maxWidth: '1200px',
        margin: '0 auto',
    },
    loading: {
        textAlign: 'center',
        padding: '40px',
        fontSize: '18px',
        direction: 'rtl',
        color: '#ffd700',
        background: 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    section: {
        marginBottom: '40px',
    },
    sectionHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
    },
    alertTitle: {
        color: '#f59e0b',
        marginBottom: '15px',
        textShadow: '0 2px 10px rgba(245, 158, 11, 0.3)',
        fontSize: '22px',
    },
    createButton: {
        padding: '12px 24px',
        background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '12px',
        cursor: 'pointer',
        fontWeight: 'bold',
        fontSize: '16px',
        transition: 'all 0.3s ease',
        boxShadow: '0 4px 15px rgba(22, 163, 74, 0.3)',
    },
    emptyText: {
        color: '#999',
        fontStyle: 'italic',
        padding: '40px',
        textAlign: 'center',
        backgroundColor: '#1a1a2e',
        borderRadius: '15px',
        border: '2px solid rgba(218, 165, 32, 0.2)',
    },
    requestsList: {
        display: 'grid',
        gap: '15px',
    },
    requestCard: {
        backgroundColor: '#1a1a2e',
        padding: '20px',
        borderRadius: '15px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '20px',
        border: '2px solid rgba(245, 158, 11, 0.5)',
        transition: 'all 0.3s ease',
    },
    requestInfo: {
        flex: 1,
    },
    requestUser: {
        margin: '0 0 5px 0',
        color: '#ffd700',
        fontSize: '18px',
    },
    requestDate: {
        margin: '5px 0',
        color: '#b0b0b0',
        fontSize: '14px',
    },
    requestMessage: {
        margin: '10px 0',
        color: '#999',
        fontSize: '14px',
        fontStyle: 'italic',
    },
    requestTime: {
        margin: '5px 0',
        color: '#777',
        fontSize: '12px',
    },
    requestActions: {
        display: 'flex',
        gap: '10px',
    },
    approveButton: {
        padding: '10px 20px',
        background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '10px',
        cursor: 'pointer',
        fontWeight: 'bold',
        transition: 'all 0.3s ease',
        boxShadow: '0 2px 8px rgba(22, 163, 74, 0.3)',
    },
    rejectButton: {
        padding: '10px 20px',
        background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '10px',
        cursor: 'pointer',
        fontWeight: 'bold',
        transition: 'all 0.3s ease',
        boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)',
    },
    editRequestButton: {
        padding: '10px 20px',
        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '10px',
        cursor: 'pointer',
        fontWeight: 'bold',
        transition: 'all 0.3s ease',
        boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)',
    },
    gamesList: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        gap: '20px',
    },
    gameCard: {
        backgroundColor: '#1a1a2e',
        padding: '20px',
        borderRadius: '15px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
        border: '2px solid rgba(218, 165, 32, 0.3)',
        transition: 'all 0.3s ease',
    },
    gameCardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '10px',
    },
    gameCardTitle: {
        margin: 0,
        color: '#ffd700',
        flex: 1,
        fontSize: '18px',
        fontWeight: 'bold',
    },
    hostBadge: {
        display: 'block',
        fontSize: '12px',
        color: '#22c55e',
        fontWeight: 'normal',
        marginTop: '5px',
    },
    hostBadgeSmall: {
        background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
        color: 'white',
        padding: '3px 8px',
        borderRadius: '6px',
        fontSize: '10px',
        marginRight: '8px',
        fontWeight: 'bold',
    },
    gameCardActions: {
        display: 'flex',
        gap: '8px',
    },
    editButton: {
        padding: '6px 12px',
        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '14px',
        transition: 'all 0.3s ease',
    },
    viewButton: {
        padding: '6px 14px',
        background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '14px',
        transition: 'all 0.3s ease',
    },
    deleteButton: {
        padding: '6px 12px',
        background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '14px',
        transition: 'all 0.3s ease',
    },
    gameCardDate: {
        color: '#b0b0b0',
        fontSize: '14px',
        margin: '5px 0',
    },
    gameCardAddress: {
        color: '#999',
        fontSize: '14px',
        margin: '5px 0',
    },
    gameCardStats: {
        display: 'flex',
        gap: '15px',
        marginTop: '10px',
        marginBottom: '10px',
        paddingTop: '10px',
        paddingBottom: '10px',
        borderTop: '1px solid rgba(218, 165, 32, 0.2)',
        borderBottom: '1px solid rgba(218, 165, 32, 0.2)',
        fontSize: '14px',
        color: '#22c55e',
        fontWeight: 'bold',
    },
    gameCardPlayers: {
        fontSize: '14px',
        color: '#999',
        marginBottom: '15px',
    },
    gameCardLink: {
        display: 'flex',
        gap: '10px',
        marginTop: '15px',
    },
    linkInput: {
        flex: 1,
        padding: '10px',
        border: '2px solid rgba(218, 165, 32, 0.3)',
        borderRadius: '8px',
        fontSize: '12px',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        color: '#e0e0e0',
        direction: 'ltr',
        textAlign: 'left',
        outline: 'none',
    },
    copyButton: {
        padding: '10px 14px',
        background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '12px',
        whiteSpace: 'nowrap',
        fontWeight: 'bold',
        transition: 'all 0.3s ease',
    },
    usersList: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
        gap: '15px',
    },
    userCard: {
        backgroundColor: '#1a1a2e',
        padding: '18px',
        borderRadius: '15px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
        border: '2px solid rgba(218, 165, 32, 0.2)',
        transition: 'all 0.3s ease',
    },
    userCardContent: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        color: '#e0e0e0',
    },
    userCardActions: {
        display: 'flex',
        gap: '10px',
        alignItems: 'center',
    },
    userEmail: {
        color: '#999',
        fontSize: '12px',
    },
    userAddress: {
        color: '#999',
        fontSize: '11px',
    },
    hostingPlayerBadgeSmall: {
        color: '#22c55e',
        fontSize: '11px',
        fontWeight: 'bold',
    },
    adminBadge: {
        background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
        color: 'white',
        padding: '4px 8px',
        borderRadius: '6px',
        fontSize: '11px',
        fontWeight: 'bold',
        boxShadow: '0 2px 8px rgba(124, 58, 237, 0.3)',
    },
    hostingTagButton: {
        padding: '6px 12px',
        background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '14px',
        transition: 'all 0.3s ease',
        opacity: 0.6,
    },
    hostingTagButtonActive: {
        padding: '6px 12px',
        background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '14px',
        transition: 'all 0.3s ease',
        boxShadow: '0 2px 8px rgba(34, 197, 94, 0.4)',
    },
    editUserButton: {
        padding: '6px 12px',
        background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '14px',
        transition: 'all 0.3s ease',
    },
    deleteUserButton: {
        padding: '6px 12px',
        background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '14px',
        transition: 'all 0.3s ease',
        boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)',
    },
    modal: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px',
    },
    modalContent: {
        backgroundColor: '#1a1a2e',
        borderRadius: '20px',
        padding: '30px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        direction: 'rtl',
        border: '2px solid rgba(218, 165, 32, 0.3)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        color: '#ffd700',
    },
    closeButton: {
        background: 'none',
        border: 'none',
        fontSize: '28px',
        cursor: 'pointer',
        color: '#999',
        transition: 'color 0.3s ease',
        padding: '0',
        lineHeight: '1',
    },
    error: {
        backgroundColor: 'rgba(220, 53, 69, 0.15)',
        color: '#ff6b6b',
        padding: '12px',
        borderRadius: '10px',
        marginBottom: '20px',
        border: '1px solid rgba(220, 53, 69, 0.3)',
        textAlign: 'center',
    },
    suggestion: {
        background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(217, 119, 6, 0.15) 100%)',
        padding: '15px',
        borderRadius: '12px',
        marginBottom: '20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '15px',
        border: '1px solid rgba(245, 158, 11, 0.3)',
        color: '#fbbf24',
    },
    suggestionButton: {
        padding: '8px 16px',
        background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        fontWeight: 'bold',
        whiteSpace: 'nowrap',
        transition: 'all 0.3s ease',
    },
    formGroup: {
        marginBottom: '20px',
    },
    label: {
        display: 'block',
        marginBottom: '8px',
        fontWeight: '600',
        color: '#e0e0e0',
        fontSize: '15px',
    },
    checkboxLabel: {
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        color: '#e0e0e0',
    },
    input: {
        width: '100%',
        padding: '14px 16px',
        border: '2px solid rgba(218, 165, 32, 0.3)',
        borderRadius: '12px',
        fontSize: '16px',
        boxSizing: 'border-box',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        color: '#ffffff',
        transition: 'all 0.3s ease',
        outline: 'none',
        colorScheme: 'dark',
    },
    helpText: {
        fontSize: '13px',
        color: '#999',
        margin: '6px 0 0 0',
        display: 'block',
    },
    playersList: {
        border: '2px solid rgba(218, 165, 32, 0.3)',
        borderRadius: '12px',
        maxHeight: '300px',
        overflow: 'auto',
        padding: '10px',
        backgroundColor: 'rgba(255, 255, 255, 0.02)',
    },
    playerCheckbox: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '10px',
        cursor: 'pointer',
        borderRadius: '8px',
        transition: 'background-color 0.2s ease',
        color: '#e0e0e0',
    },
    playerCheckboxLabel: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
    },
    playerCheckboxEmail: {
        fontSize: '12px',
        color: '#999',
    },
    modalActions: {
        display: 'flex',
        gap: '10px',
        justifyContent: 'flex-end',
        marginTop: '30px',
    },
    cancelButton: {
        padding: '12px 24px',
        background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '12px',
        cursor: 'pointer',
        fontWeight: 'bold',
        transition: 'all 0.3s ease',
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
    },
    saveButton: {
        padding: '12px 24px',
        background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '12px',
        cursor: 'pointer',
        fontWeight: 'bold',
        transition: 'all 0.3s ease',
        boxShadow: '0 4px 15px rgba(22, 163, 74, 0.3)',
    },
    infoAlert: {
        background: 'linear-gradient(135deg, rgba(22, 163, 74, 0.15) 0%, rgba(21, 128, 61, 0.15) 100%)',
        border: '2px solid rgba(22, 163, 74, 0.5)',
        padding: '15px',
        borderRadius: '12px',
        marginBottom: '20px',
        color: '#22c55e',
        fontWeight: 'bold',
    },
};
